/**
 * 动态词汇统计（旧 views/dynamic-lexemes/StatsView.vue）：四个数量卡片、高频词表、快速入口
 */
import { useEffect, type ReactNode } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { App, Button, Card, Col, Row, Statistic, Table, Tag, theme, type TableColumnsType } from 'antd'
import {
  AuditOutlined,
  BookOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons'
import { PageHeader } from '@/components/PageHeader'
import type { TopEncounterWord } from '@/types/dynamic-lexemes'
import { languageColor, languageName } from './constants'
import { useLexemeStats } from './hooks'
import styles from './DynamicLexemes.module.css'

export function StatsPage() {
  const { message } = App.useApp()
  const { token } = theme.useToken()
  const navigate = useNavigate()
  const { data: stats, isFetching, isError, refetch } = useLexemeStats()

  useEffect(() => {
    if (isError) message.error('获取统计数据失败')
  }, [isError, message])

  const refresh = async () => {
    const result = await refetch()
    if (!result.isError) message.success('统计数据已刷新')
  }

  const goToPendingReview = () => navigate({ to: '/dynamic-lexemes/pending-review' })
  const pendingCount = stats?.pending_review_count ?? 0

  const cards: { title: string; value: number; color: string; icon: ReactNode }[] = [
    { title: '总动态词汇', value: stats?.total_dynamic_lexemes ?? 0, color: token.colorPrimary, icon: <BookOutlined /> },
    { title: '待审核', value: pendingCount, color: token.colorWarning, icon: <ClockCircleOutlined /> },
    { title: '已批准', value: stats?.approved_count ?? 0, color: token.colorSuccess, icon: <CheckCircleOutlined /> },
    { title: '已拒绝', value: stats?.rejected_count ?? 0, color: token.colorError, icon: <CloseCircleOutlined /> }
  ]

  const columns: TableColumnsType<TopEncounterWord> = [
    { title: '词汇', key: 'word', width: 120, render: (_, record) => <Tag color="blue">{record.word}</Tag> },
    {
      title: '语言',
      key: 'language',
      width: 80,
      render: (_, record) => <Tag color={languageColor(record.language)}>{languageName(record.language)}</Tag>
    },
    {
      title: '遇到次数',
      dataIndex: 'encounter_count',
      width: 100,
      sorter: (a, b) => a.encounter_count - b.encounter_count
    },
    { title: '用户数', dataIndex: 'user_count', width: 80, sorter: (a, b) => a.user_count - b.user_count },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: () => (
        <Button type="link" size="small" onClick={goToPendingReview}>
          审核
        </Button>
      )
    }
  ]

  return (
    <div>
      <PageHeader title="动态词汇统计" subtitle="查看动态创建词汇的整体情况和趋势分析" />

      <Row gutter={[16, 16]} className={styles.block}>
        {cards.map((card) => (
          <Col key={card.title} xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title={card.title}
                value={card.value}
                prefix={card.icon}
                styles={{ content: { color: card.color, fontWeight: 600 } }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        title="高频遇到词汇"
        className={styles.block}
        extra={
          <Button type="link" icon={<ReloadOutlined />} onClick={() => void refresh()}>
            刷新
          </Button>
        }
      >
        <Table<TopEncounterWord>
          rowKey={(record) => `${record.language}:${record.word}`}
          columns={columns}
          dataSource={stats?.top_encounter_words ?? []}
          pagination={false}
          size="small"
          loading={isFetching}
        />
      </Card>

      <Card title="快速操作">
        <Row gutter={16}>
          <Col span={8}>
            <Button type="primary" block icon={<AuditOutlined />} disabled={!pendingCount} onClick={goToPendingReview}>
              审核待审词汇 ({pendingCount})
            </Button>
          </Col>
          <Col span={8}>
            <Button block icon={<CheckCircleOutlined />} onClick={() => navigate({ to: '/dynamic-lexemes/approved' })}>
              查看已批准词汇
            </Button>
          </Col>
          <Col span={8}>
            <Button block icon={<SearchOutlined />} onClick={() => navigate({ to: '/dynamic-lexemes/search' })}>
              搜索词汇
            </Button>
          </Col>
        </Row>
      </Card>
    </div>
  )
}
